// What Misti's face should look like for a given conversation state and emotion
// - pure data, no DOM.
//
// Modelled on the screen of Pudu's BellaBot (the cat shape is the robot's
// shell; the screen shows only the face): glossy dark eyes ringed in warm white
// with two highlights, small arched brows, a little "ω" cat mouth and whiskers,
// on a dark screen with no colour in the face. The dark eyes with highlights are
// what keep it friendly - faces without pupils are rated unfriendly and
// machine-like (Kalegina 2018). Emotion is carried by the eyes' SHAPE ("^ ^",
// the "<" wink, drooping lids), the brows, the whiskers, where she looks and how
// the whole face moves. Colour appears only in the thin status glow on the
// screen edge.
//
// The renderer springs its current values toward these targets every frame, so
// nothing ever snaps.

export const DEFAULTS = {
	// Eye modes, as crossfade weights. The renderer normalises them, so a target
	// only has to name the mode it wants. A closed mode squeezes the open eye
	// shut first and only then fades its line in, so a line never ghosts over a
	// half-visible eye.
	open: 1, // the open eye
	arcUp: 0, // "^ ^" - eyes closed from smiling
	arcSoft: 0, // "u" - eyes softly shut, for thanks and goodbyes
	arcDown: 0, // a low flat line - asleep
	winkR: 0, // right eye squeezed into a "<", independent of the modes
	squeeze: 0, // "> <" - both eyes squeezed tight shut (crying)

	eyeScale: 1,
	eyeTall: 1, // vertical stretch - surprise makes eyes taller, not just bigger
	eyeAsym: 0, // + makes the right eye bigger and the left smaller (curious)
	lidTop: 0, // upper lid, 0 open .. 1 shut
	sadLids: 0, // 0 .. 1, outer corners of the top edge drop
	madLids: 0, // 0 .. 1, INNER corners of the top edge drop - a cross, stern look
	squintR: 0, // extra closure on the right eye only
	lookX: 0, // -1 .. 1
	lookY: 0, // -1 .. 1

	browY: 0, // px, + lowers both brows
	browAng: 0, // degrees, + lifts the INNER ends (worried, sorry)
	browRY: 0, // extra lift for the right brow alone (curious: one raised brow)
	whisk: 0, // whiskers: + perk up (pleased), - droop (sorry, sleepy)

	headTilt: 0, // degrees - the whole face rotates, like tilting a head
	headX: 0, // px in the 1600x1000 viewBox
	headY: 0, // px; + is down
	bright: 1, // the whole face dims when she is drowsy or asleep

	smile: 0.7, // 1 smile .. 0 flat .. -1 frown
	mouthOpen: 0, // how far the jaw drops - from her voice, or a laugh
	mouthWide: 0.5, // 1 spread wide ("ee", a grin) .. 0 gathered round ("oo")
	talk: 0, // 1 swaps the "ω" for the talking mouth
	mouthW: 1, // mouth width
	mouthTilt: 0, // the askew "pardon?" mouth
	mouthWave: 0, // an S-bend - the uneasy mouth of an error
	dots: 0, // the mouth is replaced by three dots - looking something up
	tears: 0, // tears falling from her eyes
	huff: 0, // frustration: a cross mark and a puff of steam
};

// Spring speed per channel. Eyes are quick like a real glance; the face moves
// like a head - heavier.
export const STIFFNESS = {
	open: 10,
	arcUp: 10,
	arcSoft: 8,
	arcDown: 6,
	winkR: 14,
	squeeze: 12,
	eyeScale: 12,
	eyeTall: 12,
	eyeAsym: 9,
	lidTop: 14,
	sadLids: 8,
	madLids: 9,
	squintR: 10,
	lookX: 11,
	lookY: 11,
	browY: 10,
	browAng: 9,
	browRY: 10,
	whisk: 7,
	headTilt: 5,
	headX: 5,
	headY: 6,
	bright: 3,
	smile: 8,
	mouthOpen: 26,
	mouthWide: 20,
	talk: 12,
	mouthW: 8,
	mouthTilt: 8,
	mouthWave: 8,
	dots: 9,
	tears: 5,
	huff: 6,
};

// ---------------------------------------------------------------------------
// Conversation states - the baseline. An emotion is layered on top.
// ---------------------------------------------------------------------------
const STATE = {
	asleep: {
		browY: 16,
		whisk: -0.6,
		open: 0,
		arcDown: 1,
		bright: 0.5,
		headY: 28,
		smile: 0.3,
		mouthW: 0.7,
	},
	drowsy: {
		browY: 14,
		browAng: -3,
		whisk: -0.4,
		lidTop: 0.4,
		lookY: 0.3,
		headY: 14,
		smile: 0.35,
		mouthW: 0.9,
		bright: 0.8,
	},
	idle: {},
	waking: {
		// Eyes pop open and the face lifts - someone who heard their name.
		browY: -22,
		whisk: 0.4,
		eyeScale: 1.08,
		eyeTall: 1.06,
		headY: -16,
	},
	listening: {
		// Attentive: taller eyes locked on the guest, the face lifted towards them.
		browY: -14,
		whisk: 0.2,
		eyeScale: 1.06,
		eyeTall: 1.06,
		headY: -12,
		smile: 0.45,
	},
	hearing: {
		// Everything from listening, plus a nod the renderer adds - "yes, go on".
		browY: -16,
		whisk: 0.2,
		eyeScale: 1.06,
		eyeTall: 1.03,
		headY: -12,
		headTilt: -3,
		smile: 0.45,
	},
	thinking: {
		// Unmistakably NOT listening: gaze up and away, the face tilted.
		browY: -4,
		browRY: -20,
		lookX: 0.7,
		lookY: -0.62,
		eyeScale: 0.97,
		headTilt: 4,
		headX: -14,
		smile: 0.15,
		mouthW: 0.8,
	},
	working: {
		// Looking something up: gaze down, reading side to side (the renderer
		// sweeps it), and three dots in place of the mouth.
		browY: 8,
		browAng: -5,
		lookY: 0.55,
		lidTop: 0.16,
		headY: 10,
		dots: 1,
	},
	speaking: {
		// The mouth follows her live voice.
		talk: 1,
		browY: -6,
		smile: 0.6,
	},
	error: {
		browAng: 16,
		browY: 6,
		whisk: -0.7,
		lidTop: 0.1,
		sadLids: 0.2,
		lookY: 0.08,
		eyeScale: 0.97,
		headY: 4,
		smile: 0,
		mouthWave: 1,
		mouthW: 0.9,
		bright: 0.9,
	},
	reconnecting: {
		browAng: 8,
		whisk: -0.3,
		lidTop: 0.12,
		eyeScale: 0.95,
		lookX: 0.3,
		smile: 0.1,
		mouthW: 0.7,
		bright: 0.85,
	},
};

// ---------------------------------------------------------------------------
// Emotions, layered over the state. Only the named channels are overridden.
// ---------------------------------------------------------------------------
const EMOTION = {
	happy: {
		browY: -12,
		whisk: 1,
		open: 0,
		arcUp: 1,
		smile: 1,
		headY: -8,
	},
	excited: {
		// The greeting: wide eyes, an open smile and two little hops.
		talk: 1,
		mouthWide: 0.85,
		browY: -26,
		whisk: 1,
		eyeScale: 1.12,
		eyeTall: 0.95,
		headY: -14,
		smile: 1,
		mouthOpen: 0.6,
		mouthW: 1.15,
	},
	delighted: {
		talk: 1,
		mouthWide: 0.85,
		browY: -22,
		whisk: 1,
		open: 0,
		arcUp: 1,
		eyeScale: 1.06,
		smile: 1,
		mouthOpen: 0.5,
		mouthW: 1.1,
		headY: -12,
	},
	grateful: {
		// Eyes softly shut and a small bow - a namaste dip. This is also her
		// goodbye to departing families.
		browAng: 8,
		browY: -4,
		whisk: 0.5,
		open: 0,
		arcSoft: 1,
		smile: 0.85,
		headY: 26,
		headTilt: -5,
	},
	love: {
		// Nothing she says triggers this on its own (see emotion.js); it is here
		// for someone driving her by teleop.
		talk: 1,
		mouthWide: 0.7,
		browY: -14,
		whisk: 0.8,
		open: 0,
		arcUp: 1,
		smile: 1,
		mouthOpen: 0.22,
		headTilt: 8,
		headY: -6,
	},
	curious: {
		// A tilted head and one eye a little bigger.
		browY: -8,
		browRY: -26,
		whisk: 0.3,
		eyeScale: 1.05,
		eyeAsym: 0.12,
		headTilt: -11,
		headX: 16,
		lookX: 0.15,
		lookY: -0.15,
		smile: 0.45,
	},
	confused: {
		// "Pardon?" - leaning in, head tilted, the mouth askew.
		browAng: 12,
		browRY: -18,
		squintR: 0.12,
		eyeScale: 1.04,
		headTilt: 12,
		headY: -10,
		smile: 0,
		mouthTilt: 0.9,
		mouthW: 0.85,
	},
	apologetic: {
		// Outer corners droop, gaze drops, the face bows. Never a smile - smiling
		// through "sorry, we're out of that" reads as mocking the guest.
		browAng: 18,
		browY: 6,
		whisk: -1,
		sadLids: 0.85,
		lidTop: 0.08,
		lookY: 0.35,
		headY: 18,
		headTilt: -4,
		eyeScale: 0.97,
		smile: -0.4,
		mouthW: 0.85,
	},
	surprised: {
		// Hearing her name: eyes pop, brows fly up, a little hop - and a smile,
		// not a gaping "o". She is pleased to be called, not startled.
		eyeScale: 1.06,
		eyeTall: 1.14,
		browY: -30,
		headY: -16,
		smile: 0.9,
		whisk: 0.6,
	},
	shy: {
		browAng: 10,
		whisk: 0.4,
		lidTop: 0.08,
		lookX: -0.55,
		lookY: 0.45,
		eyeScale: 0.95,
		headTilt: -8,
		headX: -24,
		headY: 6,
		smile: 0.7,
		mouthW: 0.85,
	},
	wink: {
		// When a guest pats her head: the right eye squeezes shut into a "<" -
		// BellaBot's own wink.
		browRY: 14,
		browY: -10,
		whisk: 0.8,
		winkR: 1,
		headTilt: -6,
		eyeScale: 1.03,
		smile: 1,
	},
	smile: {
		// Her serving smile - "here you go, enjoy!": bright open eyes looking up
		// at the guest, raised brows, an open smile, whiskers up and a little bow
		// as she presents it (the renderer adds the bow).
		eyeScale: 1.04,
		browY: -16,
		whisk: 0.8,
		talk: 1,
		mouthOpen: 0.42,
		mouthWide: 0.95,
		smile: 1,
		headTilt: -5,
		lookY: -0.12,
	},
	sad: {
		// Bad news - sold out, a guest leaving without ordering. Brows up in the
		// middle, eyes droop and look down, a frown, the head and whiskers drop.
		sadLids: 0.75,
		lidTop: 0.1,
		browAng: 20,
		browY: 8,
		lookY: 0.5,
		eyeScale: 0.96,
		headY: 22,
		headTilt: -3,
		smile: -0.75,
		mouthW: 0.8,
		whisk: -1,
	},
	cry: {
		// The strongest sadness: eyes squeezed shut "> <", tears, a wobbling
		// frown and small sobs (the renderer adds the sobs and the wobble).
		open: 0,
		squeeze: 1,
		tears: 1,
		browAng: 24,
		browY: 6,
		headY: 16,
		smile: -1,
		mouthW: 1.15,
		whisk: -1,
	},
	frustrated: {
		// "Hmph!" - cross but cute: brows pulled down in the middle, stern lids,
		// a tight wavy mouth, a cross mark and a puff of steam, and a quick shake
		// of the head (added by the renderer). For a blocked path or a joke -
		// never aimed at a guest.
		madLids: 0.5,
		lidTop: 0.1,
		browAng: -20,
		browY: 12,
		smile: -0.3,
		mouthWave: 0.6,
		mouthW: 0.75,
		whisk: -0.2,
		huff: 1,
		headY: 4,
	},
};

// The screen-edge glow - conversation status, readable from across the room and
// with the sound off.
const AURA = {
	listening: "listen",
	hearing: "hear",
	thinking: "think",
	working: "work",
	speaking: "speak",
	error: "error",
	reconnecting: "connect",
};

export function targetFor(state, emotion) {
	return { ...DEFAULTS, ...(STATE[state] || {}), ...((emotion && EMOTION[emotion]) || {}) };
}

export function auraFor(state) {
	return AURA[state] || null;
}

export const KNOWN_STATES = Object.keys(STATE);
export const KNOWN_EMOTIONS = Object.keys(EMOTION);
